import { useEffect, useState } from "react";
import { Clock, Hourglass, UserCog } from "lucide-react";

import type { PhaseCountdownKind } from "@/api/types";

/**
 * Format an integer seconds-remaining as `mm:ss` (or `Xs` for sub-
 * minute values). Negative inputs collapse to `0s` so an expired
 * deadline doesn't render as `-5s`.
 */
function formatSeconds(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return "0s";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

interface CountdownProps {
  kind: PhaseCountdownKind;
  /**
   * Explicit deadline timestamp (ISO 8601). Used for
   * `live_seconds` countdowns: `next_retry_after` while in
   * `retrying`, `next_probe_after` while in
   * `needs_attention(attention_external)`.
   */
  deadline?: string | null;
  /**
   * Wall-clock the phase was entered. Used by the `elapsed_only`
   * variant to render `mm:ss` since transition + the late-warning
   * highlight when past `expectedSeconds`.
   */
  phaseChangedAt?: string | null;
  /**
   * Soft upper bound for `elapsed_only` highlighting and the
   * `live_seconds` "longest expected" hint.
   */
  expectedSeconds?: number | null;
  /**
   * Short imperative the parent already loaded from
   * `PoolPhaseRef.next_action_label`. The countdown widget renders
   * it after the timer so the operator sees timer + action together.
   */
  nextActionLabel?: string;
}

/**
 * Single source of truth for the small "what is the controller
 * doing & when" widget the PhaseCard renders below the badge.
 *
 * Four rendering modes selected by `kind`:
 *
 * - `live_seconds`  — counts down to `deadline`. Re-renders every
 *   second.
 * - `tick_boundary` — static "next reconcile tick (~30 s)" copy;
 *   used for phases that advance on every tick.
 * - `elapsed_only`  — counts *up* from `phaseChangedAt`. Highlights
 *   past `expectedSeconds`.
 * - `operator_wait` — no timer; renders "waiting for operator"
 *   copy. Used for `serving`, `stopped`, and
 *   `needs_attention(attention_spec)`.
 */
export function PhaseCountdown({
  kind,
  deadline,
  phaseChangedAt,
  expectedSeconds,
  nextActionLabel,
}: CountdownProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (kind === "operator_wait" || kind === "tick_boundary") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [kind]);

  if (kind === "operator_wait") {
    return (
      <div className="phase-countdown phase-countdown--wait">
        <UserCog size={14} />
        <span>{nextActionLabel ?? "Waiting for operator"}</span>
      </div>
    );
  }
  if (kind === "tick_boundary") {
    return (
      <div className="phase-countdown phase-countdown--tick">
        <Hourglass size={14} />
        <span>
          Next reconcile tick (~30s)
          {nextActionLabel ? ` · ${nextActionLabel}` : ""}
        </span>
      </div>
    );
  }
  if (kind === "live_seconds") {
    const ms = deadline ? Date.parse(deadline) : NaN;
    if (!Number.isFinite(ms)) {
      return (
        <div className="phase-countdown phase-countdown--tick">
          <Hourglass size={14} />
          <span>Next attempt scheduled</span>
        </div>
      );
    }
    const remaining = Math.max(0, Math.floor((ms - now) / 1000));
    return (
      <div className="phase-countdown phase-countdown--live">
        <Clock size={14} />
        <span>
          Next attempt in <strong>{formatSeconds(remaining)}</strong>
          {nextActionLabel ? ` · ${nextActionLabel}` : ""}
        </span>
      </div>
    );
  }
  // elapsed_only
  const start = phaseChangedAt ? Date.parse(phaseChangedAt) : NaN;
  if (!Number.isFinite(start)) {
    return (
      <div className="phase-countdown phase-countdown--tick">
        <Hourglass size={14} />
        <span>{nextActionLabel ?? "In progress"}</span>
      </div>
    );
  }
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const late =
    typeof expectedSeconds === "number"
    && expectedSeconds > 0
    && elapsed > expectedSeconds;
  return (
    <div
      className={`phase-countdown phase-countdown--elapsed${
        late ? " phase-countdown--late" : ""
      }`}
    >
      <Hourglass size={14} />
      <span>
        Elapsed <strong>{formatSeconds(elapsed)}</strong>
        {typeof expectedSeconds === "number" && expectedSeconds > 0 && (
          <>
            {" "}
            / expected ~{formatSeconds(expectedSeconds)}
          </>
        )}
        {nextActionLabel ? ` · ${nextActionLabel}` : ""}
      </span>
    </div>
  );
}
